/**
 * PATH: packages/email-hub/src/feature/components/SavedSearchesMenu.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-SAVEDSEARCHESMENU-001
 * PURPOSE: Bookmark affordance next to the inbox search bar (G8) ?
 *          save the active query under a name, list/apply/delete
 *          saved searches from a small dropdown. localStorage only.
 * WHY: Operator-laden queries ("from:simpro has:attachment") get
 *      re-typed daily; this is the cheapest persistent slice with no
 *      backend. LANDMINE: uses onMouseDown (not onClick) inside the
 *      dropdown so selection wins the race against the search
 *      input's blur ? same combobox idiom as EmailSearchSuggestions.
 * RELATED:
 *  - lib/savedSearches.ts: the pure store (unit-tested).
 */

import { useEffect, useRef, useState } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import { Bookmark, BookmarkPlus, Trash2 } from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import {
  deleteSavedSearch,
  loadSavedSearches,
  saveSearch,
  type SavedSearch,
} from '../lib/savedSearches'

interface SavedSearchesMenuProps {
  organisationId: number | null | undefined
  /** The CURRENT (live) query in the search input. */
  query: string
  /** Apply a saved query back into the search input. */
  onApply: (query: string) => void
}

export function SavedSearchesMenu({
  organisationId,
  query,
  onApply,
}: SavedSearchesMenuProps) {
  const [open, setOpen] = useState(false)
  const [saved, setSaved] = useState<SavedSearch[]>(() => loadSavedSearches(organisationId))
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)

  // Re-load when the org changes (mailbox/org switch mid-session).
  useEffect(() => {
    setSaved(loadSavedSearches(organisationId))
  }, [organisationId])

  // Light dismiss ? click anywhere outside closes the dropdown.
  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(open, () => {
    setOpen(false)
    setNaming(false)
  }, [rootRef])

  const activeQuery = query.trim()
  const canSave = activeQuery.length > 0

  const commitSave = () => {
    const finalName = name.trim() || activeQuery
    setSaved(saveSearch(organisationId, finalName, activeQuery))
    setNaming(false)
    setName('')
  }

  return (
    <div className="relative shrink-0" ref={rootRef}>
      <button
        type="button"
        onClick={() => {
          setOpen(v => !v)
          setNaming(false)
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        title={canSave ? 'Saved searches ? save the current query or apply one' : 'Saved searches'}
        data-testid="email-saved-searches-button"
        className={cn(
          'flex items-center justify-center h-7 w-7 border rounded-md transition-colors',
          open || saved.length > 0
            ? 'border-primary/40 text-primary hover:bg-primary/10'
            : 'border-border text-muted-foreground hover:bg-accent',
        )}
      >
        <Bookmark className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          data-testid="email-saved-searches-menu"
          className="absolute right-0 top-full mt-1 w-64 z-30 rounded-md border border-border bg-popover text-popover-foreground shadow-md p-1"
        >
          {canSave && !naming && (
            <button
              type="button"
              role="menuitem"
              onMouseDown={e => {
                e.preventDefault()
                setNaming(true)
                setName('')
              }}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-accent text-left"
              data-testid="email-saved-searches-save"
            >
              <BookmarkPlus className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="truncate">
                Save ?{activeQuery.length > 28 ? `${activeQuery.slice(0, 28)}?` : activeQuery}?
              </span>
            </button>
          )}
          {naming && (
            <div className="flex items-center gap-1 px-2 py-1.5">
              <input
                autoFocus
                type="text"
                value={name}
                placeholder="Name this search?"
                onChange={e => setName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') commitSave()
                  if (e.key === 'Escape') setNaming(false)
                }}
                className="flex-1 px-1.5 py-1 text-xs border border-border rounded bg-background focus:outline-none focus:ring-1 focus:ring-primary/50"
                data-testid="email-saved-searches-name-input"
              />
              <button
                type="button"
                onClick={commitSave}
                className="px-2 py-1 text-xs rounded bg-primary/10 text-primary hover:bg-primary/20"
              >
                Save
              </button>
            </div>
          )}
          {saved.length === 0 && !canSave && (
            <div className="px-2 py-2 text-xs text-muted-foreground">
              Type a search, then save it here for one-click reuse.
            </div>
          )}
          {saved.map(s => (
            <div
              key={s.id}
              className="group flex items-center gap-1 rounded hover:bg-accent"
            >
              <button
                type="button"
                role="menuitem"
                onMouseDown={e => {
                  e.preventDefault()
                  onApply(s.query)
                  setOpen(false)
                }}
                className="flex-1 min-w-0 px-2 py-1.5 text-left"
                title={s.query}
              >
                <span className="block text-xs font-medium truncate">{s.name}</span>
                <span className="block text-[10px] text-muted-foreground truncate">{s.query}</span>
              </button>
              <button
                type="button"
                onMouseDown={e => {
                  e.preventDefault()
                  setSaved(deleteSavedSearch(organisationId, s.id))
                }}
                className="h-5 w-5 mr-1 rounded flex items-center justify-center text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive hover:bg-destructive/10"
                aria-label={`Delete saved search ${s.name}`}
                title="Delete"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
